import type { ComponentChildren } from 'preact'
import { text } from '#contract'
import type { InventorySide, LocalizedText, ViewState } from '#contract'
import { useClient } from '../app/client-context.ts'
import { useView } from '../app/use-view.ts'
import { useT } from '../i18n/index.ts'
import styles from './inventory-grid.module.css'
import { useScreenUi, targetKey } from './screen-context.ts'
import { Slot } from './slot.tsx'
import { pressSlot } from './slot-press.ts'

/** Each side's inventory view on the open screen. */
export function sideView(view: ViewState, side: InventorySide) {
  const inventory = view.inventory
  return side === 'player' ? (inventory?.quickAccess ?? null) : (inventory?.screen?.other ?? null)
}

interface InventoryGridProps {
  readonly side: InventorySide
  /** Beside the title: the touch Quick move toggle. */
  readonly actions?: ComponentChildren
}

/** One side's panel: its title, what it takes, and its slots five to a row. */
export function InventoryGrid({ side, actions }: InventoryGridProps) {
  const t = useT()
  const title = useView((view) => sideView(view, side)?.title ?? null)
  const accepts = useView((view) => sideView(view, side)?.accepts ?? null)
  const size = useView((view) => sideView(view, side)?.slots.length ?? 0)
  // Shown in the danger colour while the cursor holds something it refuses: the why.
  const refusing = useView((view) => refusesCursor(view, side))
  if (title === null) return null

  return (
    <section class={styles.panel} aria-label={t(title)}>
      <header class={styles.header}>
        <h2 class={styles.title}>{t(title)}</h2>
        {accepts && <Accepts text={accepts} refusing={refusing} />}
        {actions && <div class={styles.actions}>{actions}</div>}
      </header>
      <div class={styles.grid} role="grid">
        {Array.from({ length: size }, (_, slot) => (
          <GridSlot key={slot} side={side} slot={slot} refusing={refusing} />
        ))}
      </div>
    </section>
  )
}

function Accepts({ text, refusing }: { readonly text: LocalizedText; readonly refusing: boolean }) {
  const t = useT()
  return (
    <p class={styles.accepts} data-refusing={refusing || undefined}>
      {t(text)}
    </p>
  )
}

/** Whether `side` won't take the stack on the cursor. */
function refusesCursor(view: ViewState, side: InventorySide): boolean {
  const held = view.inventory?.screen?.cursor?.item.id
  return held !== undefined && (sideView(view, side)?.refuses.includes(held) ?? false)
}

interface GridSlotProps {
  readonly side: InventorySide
  readonly slot: number
  readonly refusing: boolean
}

function GridSlot({ side, slot, refusing }: GridSlotProps) {
  const client = useClient()
  const ui = useScreenUi()
  const t = useT()
  const view = useView((state) => sideView(state, side)?.slots[slot] ?? null)
  const gamepad = useView((state) => state.input.device === 'gamepad')
  const target = { side, slot }

  return (
    <button
      type="button"
      class={styles.slot}
      data-target={targetKey(target)}
      aria-label={t(view ? view.item.name : text('inventory.empty'))}
      onPointerDown={(event) => {
        if (client) pressSlot(event, target, client.inventory, ui)
      }}
      onPointerEnter={(event) => {
        if (event.pointerType !== 'touch') ui.setHover({ target, element: event.currentTarget })
      }}
      onPointerLeave={(event) => {
        ui.leave(event.currentTarget)
      }}
      // A gamepad's focus is its pointer; a mouse click's focus isn't.
      onFocus={(event) => {
        if (gamepad) ui.setHover({ target, element: event.currentTarget })
      }}
      onBlur={(event) => {
        ui.leave(event.currentTarget)
      }}
    >
      <Slot slot={view} refused={refusing} pop />
    </button>
  )
}
